import { useState } from 'react';
import type { ComparedJourney, ComparedStep, IndicatorDelta } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { DirectionChip } from './DirectionChip.js';
import { displayFieldLabel } from '../../shared/ui/field-label.js';
import { COLUMN_HINTS, COLUMN_LABELS, INDICATOR_LABELS, formatIndicatorValue } from './comparison-format.js';
import { LowSampleBadge } from '../../shared/ui/LowSampleBadge.js';

/** Step indicators shown as columns, in this order. */
const STEP_COLUMNS: ReadonlyArray<IndicatorDelta['key']> = [
  'drop_off_rate',
  'friction_score',
  'median_duration_ms',
  'hesitations_per_session',
  'errors_per_session',
];

/** Field indicators shown under a step, mapped onto the same columns. */
const FIELD_COLUMNS: ReadonlyArray<IndicatorDelta['key']> = [
  'abandon_rate',
  'friction_score',
  'median_duration_ms',
  'hesitations_per_session',
  'error_rate',
];

function CompareCell({ delta }: { delta: IndicatorDelta | undefined }) {
  if (!delta) return <span className="cell-muted">—</span>;
  return (
    <span className="compare-cell" data-indicator={delta.key}>
      <span className="compare-cell__values">
        <span className="compare-cell__before">{formatIndicatorValue(delta.beforeValue, delta.unit)}</span>
        <span className="compare-cell__arrow" aria-hidden>
          →
        </span>
        <span className="compare-cell__after">{formatIndicatorValue(delta.afterValue, delta.unit)}</span>
      </span>
      <DirectionChip delta={delta} />
    </span>
  );
}

function Sessions({ before, after }: { before: number; after: number }) {
  return (
    <span className="compare-cell__values">
      <span className="compare-cell__before">{before.toLocaleString('fr-FR')}</span>
      <span className="compare-cell__arrow" aria-hidden>
        →
      </span>
      <span className="compare-cell__after">{after.toLocaleString('fr-FR')}</span>
    </span>
  );
}

const byKey = (indicators: readonly IndicatorDelta[], key: IndicatorDelta['key']) =>
  indicators.find((delta) => delta.key === key);

function StepRows({ step }: { step: ComparedStep }) {
  const [open, setOpen] = useState(false);
  const fieldCount = step.fields.length;
  return (
    <>
      <tr data-step-id={step.stepId} data-guided={step.hasGuidance}>
        <td>
          <div className="compare-table__step">
            <span className="compare-table__position">{step.position}</span>
            <span className="compare-table__name">{step.stepName}</span>
            {step.hasGuidance && (
              <span title="Une aide publiée dans cette version s'affiche sur cette étape.">
                <Badge tone="brand">Aide publiée ici</Badge>
              </span>
            )}
            {step.lowConfidence && <LowSampleBadge />}
            {fieldCount > 0 && (
              <button
                type="button"
                className="compare-table__toggle"
                aria-expanded={open}
                aria-label={`${open ? 'Masquer' : 'Afficher'} les champs de l'étape ${step.stepName}`}
                onClick={() => setOpen((value) => !value)}
              >
                {open ? 'Masquer' : 'Voir'} {fieldCount > 1 ? `les ${fieldCount} champs` : 'le champ'}
              </button>
            )}
          </div>
        </td>
        <td style={{ textAlign: 'end' }}>
          <Sessions before={step.beforeSessions} after={step.afterSessions} />
        </td>
        {STEP_COLUMNS.map((key) => (
          <td key={key} style={{ textAlign: 'end' }}>
            <CompareCell delta={byKey(step.indicators, key)} />
          </td>
        ))}
      </tr>
      {open &&
        step.fields.map((field) => (
          <tr key={field.fieldKey} className="compare-table__field" data-field-key={field.fieldKey}>
            <td>
              <span className="compare-table__field-name" title={field.fieldKey}>
                {`⁨${displayFieldLabel(field.fieldLabel, field.fieldKey)}⁩`}
              </span>
              {field.lowConfidence && (
                <>
                  {' '}
                  <LowSampleBadge />
                </>
              )}
            </td>
            <td style={{ textAlign: 'end' }}>
              <Sessions before={field.beforeSessions} after={field.afterSessions} />
            </td>
            {FIELD_COLUMNS.map((key) => (
              <td key={key} style={{ textAlign: 'end' }}>
                <CompareCell delta={byKey(field.indicators, key)} />
              </td>
            ))}
          </tr>
        ))}
    </>
  );
}

/**
 * The same indicator on both sides, step by step then field by field
 * (functional §5.4 "Comparaison avant et après"). Everything shown comes from
 * the API; the table computes nothing (technical §18.3).
 */
export function ComparisonTable({ journey }: { journey: ComparedJourney }) {
  if (journey.steps.length === 0) {
    return <p className="comparison__empty">Aucune étape mesurée des deux côtés pour ce parcours.</p>;
  }
  return (
    <div className="table__scroll">
      <table className="table compare-table" data-journey-id={journey.journeyId}>
        <thead>
          <tr>
            <th>Étape · champ</th>
            <th
              style={{ textAlign: 'end' }}
              title="Usagers mesurés sur l'étape, avant puis après la publication."
            >
              Usagers
            </th>
            {STEP_COLUMNS.map((key) => (
              <th
                key={key}
                style={{ textAlign: 'end' }}
                title={COLUMN_HINTS[key]}
                className="table__th--hint"
              >
                {COLUMN_LABELS[key] ?? INDICATOR_LABELS[key]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {journey.steps.map((step) => (
            <StepRows key={step.stepId} step={step} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
